import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search, Check, X, Phone, Mail, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import insurers from "@/lib/insurers.json";

/**
 * InsurerCombobox — searchable dropdown of all 229 known German Haftpflicht-
 * Versicherungen with full contact info (address, phone, email).
 *
 * Filtering: only entries whose name STARTS WITH the user's query are shown
 * (case-insensitive). Free-text input is preserved for niche/foreign carriers.
 *
 * UX:
 *  • Each list item shows: name, address, phone, email
 *  • When the selected name is an exact match in the list, a contact-info
 *    panel is rendered directly below the input → click-to-call/mail.
 */
export default function InsurerCombobox({
  value = "",
  onChange,
  placeholder = "Versicherer eingeben oder aus Liste wählen…",
  testid = "insurer-combobox",
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const wrapRef = useRef(null);

  // Sync external value → query (e.g. when GDV-Abfrage fills it)
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return insurers;
    return insurers.filter((i) => i.name.toLowerCase().startsWith(q));
  }, [query]);

  const selected = useMemo(
    () => insurers.find((i) => i.name.toLowerCase() === (query || "").trim().toLowerCase()) || null,
    [query]
  );

  const select = (insurer) => {
    setQuery(insurer.name);
    onChange?.(insurer.name, insurer);
    setOpen(false);
  };

  const clear = () => {
    setQuery("");
    onChange?.("", null);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange?.(e.target.value, null); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="h-11 pl-9 pr-9 text-[15px]"
          data-testid={`${testid}-input`}
        />
        {query && !disabled && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-400 hover:text-zinc-700"
            aria-label="Zurücksetzen"
            data-testid={`${testid}-clear`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Contact-info panel below the input — only when an exact match is selected */}
      {selected && !open && (
        <div className="mt-2 rounded-lg border border-cyan-100 bg-cyan-50/40 px-3 py-2.5 text-xs text-zinc-700" data-testid={`${testid}-info`}>
          <div className="flex items-start gap-2">
            <MapPin className="h-3.5 w-3.5 text-cyan-600 mt-0.5 shrink-0" />
            <span>
              {selected.street}{selected.street && ", "}{selected.postal_code} {selected.city}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
            {selected.phone && (
              <a href={`tel:${selected.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-1 text-cyan-700 hover:underline">
                <Phone className="h-3.5 w-3.5" /> {selected.phone}
              </a>
            )}
            {selected.email && (
              <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1 text-cyan-700 hover:underline">
                <Mail className="h-3.5 w-3.5" /> {selected.email}
              </a>
            )}
          </div>
        </div>
      )}

      {open && !disabled && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 rounded-xl border border-zinc-200 bg-white shadow-lg overflow-hidden max-h-[360px] overflow-y-auto"
          data-testid={`${testid}-list`}
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-zinc-500">
              Kein Treffer in der Liste — der eingegebene Text wird trotzdem als manuelle Versicherung gespeichert.
            </div>
          ) : (
            <>
              <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 border-b border-zinc-100 bg-zinc-50/60 sticky top-0 z-10">
                {filtered.length} Treffer · {insurers.length} Versicherungen gesamt
              </div>
              <ul role="listbox">
                {filtered.map((i) => {
                  const active = i.name.toLowerCase() === (query || "").trim().toLowerCase();
                  return (
                    <li
                      key={i.name}
                      role="option"
                      aria-selected={active}
                      data-testid={`${testid}-option`}
                      className={cn(
                        "px-4 py-2.5 cursor-pointer flex items-start gap-2 hover:bg-cyan-50 border-b border-zinc-50 last:border-0",
                        active && "bg-cyan-100/60"
                      )}
                      onMouseDown={(e) => { e.preventDefault(); select(i); }}
                    >
                      {active ? (
                        <Check className="h-4 w-4 text-cyan-600 shrink-0 mt-0.5" />
                      ) : (
                        <span className="h-4 w-4 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className={cn("text-sm truncate", active && "font-semibold")}>{i.name}</div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-zinc-500">
                          {(i.postal_code || i.city) && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> {i.postal_code} {i.city}
                            </span>
                          )}
                          {i.phone && (
                            <span className="inline-flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {i.phone}
                            </span>
                          )}
                          {i.email && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <Mail className="h-3 w-3" /> {i.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {!selected && query.trim() && (
            <div className="px-4 py-2.5 text-[12px] text-zinc-600 border-t border-zinc-100 bg-amber-50/40 flex items-center gap-2">
              <span className="font-semibold">Manuell:</span>
              <span className="truncate">„{query.trim()}" wird übernommen</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
